import React, { useState, useEffect } from "react";
import {
    Container,
    Paper,
    Typography,
    Box,
    TextField,
    Button,
    Divider,
    Alert,
    CircularProgress,
    Grid,
    Card,
    CardContent,
    Switch,
    FormControlLabel,
    InputAdornment,
    IconButton,
    Snackbar
} from "@mui/material";
import {
    Save as SaveIcon,
    Person as PersonIcon,
    VpnKey as VpnKeyIcon,
    Notifications as NotificationsIcon,
    Settings as SettingsIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import {
    updatePassword,
    updateEmail,
    reauthenticateWithCredential,
    EmailAuthProvider
} from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";

const EmployeeSettingsPage = ({ user, orgId }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [profileData, setProfileData] = useState({
        name: "",
        email: user?.email || "",
        phoneNumber: "",
        displayName: "",
        jobTitle: ""
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
    });

    const [notifications, setNotifications] = useState({
        emailNotifications: true,
        pushNotifications: true,
        clockInReminders: true,
        timeOffUpdates: true
    });

    const [preferences, setPreferences] = useState({
        theme: "light",
        language: "en",
        timeFormat: "12h"
    });

    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const [alert, setAlert] = useState({
        open: false,
        message: "",
        severity: "info"
    });

    const [errors, setErrors] = useState({});

    // Fetch user profile data
    useEffect(() => {
        const fetchUserData = async () => {
            if (!user || !orgId) return;

            setLoading(true);
            try {
                const userRef = doc(db, "organizations", orgId, "users", user.uid);
                const userDoc = await getDoc(userRef);

                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    setProfileData(prev => ({
                        ...prev,
                        name: userData.name || "",
                        email: user.email,
                        phoneNumber: userData.phoneNumber || "",
                        displayName: userData.displayName || "",
                        jobTitle: userData.jobTitle || ""
                    }));

                    // Set notification preferences if they exist
                    if (userData.notifications) {
                        setNotifications(prev => ({
                            ...prev,
                            ...userData.notifications
                        }));
                    }

                    // Set user preferences if they exist
                    if (userData.preferences) {
                        setPreferences(prev => ({
                            ...prev,
                            ...userData.preferences
                        }));
                    }
                }
            } catch (error) {
                console.error("Error fetching user data:", error);
                setAlert({
                    open: true,
                    message: "Failed to load user profile data",
                    severity: "error"
                });
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, [user, orgId]);

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleNotificationChange = (e) => {
        const { name, checked } = e.target;
        setNotifications(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handlePreferenceChange = (e) => {
        const { name, value } = e.target;
        setPreferences(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const togglePasswordVisibility = (field) => {
        setShowPassword(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const validateProfileData = () => {
        const newErrors = {};

        if (!profileData.name.trim()) {
            newErrors.name = "Name is required";
        }

        if (!profileData.email.includes('@')) {
            newErrors.email = "Valid email is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validatePasswordData = () => {
        const newErrors = {};

        if (!passwordData.currentPassword) {
            newErrors.currentPassword = "Current password is required";
        }

        if (passwordData.newPassword.length < 6) {
            newErrors.newPassword = "Password must be at least 6 characters";
        }

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            newErrors.confirmPassword = "Passwords do not match";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const saveProfile = async () => {
        if (!validateProfileData()) return;

        setLoading(true);
        try {
            const userRef = doc(db, "organizations", orgId, "users", user.uid);

            // Update profile in Firestore
            await updateDoc(userRef, {
                name: profileData.name,
                phoneNumber: profileData.phoneNumber,
                displayName: profileData.displayName,
                jobTitle: profileData.jobTitle,
                updatedAt: new Date()
            });

            // Update email in Firebase Auth if changed
            if (profileData.email !== user.email) {
                await updateEmail(auth.currentUser, profileData.email);

                // Also update email in userOrgs collection
                const userOrgRef = doc(db, "userOrgs", user.uid);
                await updateDoc(userOrgRef, {
                    email: profileData.email
                });
            }

            setAlert({
                open: true,
                message: "Profile updated successfully",
                severity: "success"
            });
        } catch (error) {
            console.error("Error updating profile:", error);
            setAlert({
                open: true,
                message: error.message || "Failed to update profile",
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const changePassword = async () => {
        if (!validatePasswordData()) return;

        setLoading(true);
        try {
            // Re-authenticate user
            const credential = EmailAuthProvider.credential(
                user.email,
                passwordData.currentPassword
            );

            await reauthenticateWithCredential(auth.currentUser, credential);

            // Update password
            await updatePassword(auth.currentUser, passwordData.newPassword);

            // Clear password fields
            setPasswordData({
                currentPassword: "",
                newPassword: "",
                confirmPassword: ""
            });

            setAlert({
                open: true,
                message: "Password changed successfully",
                severity: "success"
            });
        } catch (error) {
            console.error("Error changing password:", error);

            // Handle specific errors
            if (error.code === 'auth/wrong-password') {
                setErrors({ ...errors, currentPassword: "Current password is incorrect" });
            } else {
                setAlert({
                    open: true,
                    message: error.message || "Failed to change password",
                    severity: "error"
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const savePreferences = async () => {
        setLoading(true);
        try {
            const userRef = doc(db, "organizations", orgId, "users", user.uid);

            await updateDoc(userRef, {
                notifications,
                preferences,
                updatedAt: new Date()
            });

            setAlert({
                open: true,
                message: "Preferences saved successfully",
                severity: "success"
            });
        } catch (error) {
            console.error("Error saving preferences:", error);
            setAlert({
                open: true,
                message: "Failed to save preferences",
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseAlert = () => {
        setAlert({ ...alert, open: false });
    };

    return (
        <Container maxWidth="md">
            <Paper sx={{ mt: 5, p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <SettingsIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <Typography variant="h5">Employee Settings</Typography>
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                <Grid container spacing={3}>
                    {/* Profile Information */}
                    <Grid item xs={12}>
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                                    <Typography variant="h6">Profile Information</Typography>
                                </Box>
                                <Divider sx={{ mb: 3 }} />

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Full Name"
                                            name="name"
                                            value={profileData.name}
                                            onChange={handleProfileChange}
                                            error={!!errors.name}
                                            helperText={errors.name}
                                            disabled={loading}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Display Name (Nickname)"
                                            name="displayName"
                                            value={profileData.displayName}
                                            onChange={handleProfileChange}
                                            disabled={loading}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            name="email"
                                            type="email"
                                            value={profileData.email}
                                            onChange={handleProfileChange}
                                            error={!!errors.email}
                                            helperText={errors.email}
                                            disabled={loading}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Phone Number"
                                            name="phoneNumber"
                                            value={profileData.phoneNumber}
                                            onChange={handleProfileChange}
                                            disabled={loading}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Job Title"
                                            name="jobTitle"
                                            value={profileData.jobTitle}
                                            onChange={handleProfileChange}
                                            disabled={loading}
                                        />
                                    </Grid>
                                </Grid>

                                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<SaveIcon />}
                                        onClick={saveProfile}
                                        disabled={loading}
                                    >
                                        Save Profile
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Password Change */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <VpnKeyIcon sx={{ mr: 1, color: 'primary.main' }} />
                                    <Typography variant="h6">Change Password</Typography>
                                </Box>
                                <Divider sx={{ mb: 3 }} />

                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Current Password"
                                    name="currentPassword"
                                    type={showPassword.current ? "text" : "password"}
                                    value={passwordData.currentPassword}
                                    onChange={handlePasswordChange}
                                    error={!!errors.currentPassword}
                                    helperText={errors.currentPassword}
                                    disabled={loading}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => togglePasswordVisibility('current')}
                                                    edge="end"
                                                >
                                                    {showPassword.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="New Password"
                                    name="newPassword"
                                    type={showPassword.new ? "text" : "password"}
                                    value={passwordData.newPassword}
                                    onChange={handlePasswordChange}
                                    error={!!errors.newPassword}
                                    helperText={errors.newPassword}
                                    disabled={loading}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => togglePasswordVisibility('new')}
                                                    edge="end"
                                                >
                                                    {showPassword.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Confirm New Password"
                                    name="confirmPassword"
                                    type={showPassword.confirm ? "text" : "password"}
                                    value={passwordData.confirmPassword}
                                    onChange={handlePasswordChange}
                                    error={!!errors.confirmPassword}
                                    helperText={errors.confirmPassword}
                                    disabled={loading}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => togglePasswordVisibility('confirm')}
                                                    edge="end"
                                                >
                                                    {showPassword.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={changePassword}
                                        disabled={loading}
                                    >
                                        Update Password
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Notification Settings */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <NotificationsIcon sx={{ mr: 1, color: 'primary.main' }} />
                                    <Typography variant="h6">Notification Settings</Typography>
                                </Box>
                                <Divider sx={{ mb: 3 }} />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={notifications.emailNotifications}
                                            onChange={handleNotificationChange}
                                            name="emailNotifications"
                                            color="primary"
                                            disabled={loading}
                                        />
                                    }
                                    label="Email Notifications"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={notifications.pushNotifications}
                                            onChange={handleNotificationChange}
                                            name="pushNotifications"
                                            color="primary"
                                            disabled={loading}
                                        />
                                    }
                                    label="Push Notifications"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={notifications.clockInReminders}
                                            onChange={handleNotificationChange}
                                            name="clockInReminders"
                                            color="primary"
                                            disabled={loading}
                                        />
                                    }
                                    label="Clock-in Reminders"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={notifications.timeOffUpdates}
                                            onChange={handleNotificationChange}
                                            name="timeOffUpdates"
                                            color="primary"
                                            disabled={loading}
                                        />
                                    }
                                    label="Time-off Request Updates"
                                />

                                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={savePreferences}
                                        disabled={loading}
                                    >
                                        Save Preferences
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                    <Button
                        variant="outlined"
                        onClick={() => navigate("/employee")}
                    >
                        Back to Dashboard
                    </Button>
                </Box>
            </Paper>

            <Snackbar
                open={alert.open}
                autoHideDuration={6000}
                onClose={handleCloseAlert}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
                    {alert.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default EmployeeSettingsPage;